import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Backdrop,


  
  Card,
  CardContent,
  TextField,
  SelectChangeEvent,
  TableSortLabel,
  IconButton,
  Tooltip,
  Link,
  Chip,
} from '@mui/material';
import { ArrowUpward, ArrowDownward, FileDownload, Link as LinkIcon } from '@mui/icons-material';
import { getAmazonProductUrl } from '../utils/marketUtils';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import type {
  CompetitorAnalysisResponse,
  ProductCompetitor,
} from '../types/index';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

const CompetitorAnalysis: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CompetitorAnalysisResponse | null>(null);
  const [competitors, setCompetitors] = useState<ProductCompetitor[]>([]);
  const [selectedCompetitors, setSelectedCompetitors] = useState<string>('');
  const [days, setDays] = useState(90);
  const [minSearchVolume, setMinSearchVolume] = useState(100);
  const [maxPosition, setMaxPosition] = useState(100);
  const [market, setMarket] = useState<number | ''>(1); // Default to US market
  const [showPositionChart, setShowPositionChart] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [keywordFilter, setKeywordFilter] = useState<'all' | 'winning' | 'losing'>('all');
  const [applyLoading, setApplyLoading] = useState(false);
  
  // Sorting states for keyword comparison table
  const [keywordSortBy, setKeywordSortBy] = useState<string>('');
  const [keywordSortDirection, setKeywordSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Sorting states for top opportunities table
  const [opportunitySortBy, setOpportunitySortBy] = useState<string>('');
  const [opportunitySortDirection, setOpportunitySortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Export states
  const [isExportingKeywords, setIsExportingKeywords] = useState(false);
  const [isExportingOpportunities, setIsExportingOpportunities] = useState(false);

  const currentCompetitor = competitors.find(c => c.id.toString() === selectedCompetitors);

  useEffect(() => {
    fetchCompetitors();
  }, [productId]);

  useEffect(() => {
    if (competitors.length > 0 && selectedCompetitors && !data) {
      fetchCompetitorAnalysis();
    }
  }, [competitors.length, selectedCompetitors]);

  const fetchCompetitors = async () => {
    try {
      console.log('Fetching competitors for product:', productId);
      const competitors = await api.getProductCompetitors(parseInt(productId!));
      console.log('Competitors fetched:', competitors);
      setCompetitors(competitors);
      // Select first competitor by default
      if (competitors.length > 0) {
        const firstCompetitorId = competitors[0].id.toString();
        console.log('Setting selected competitor:', firstCompetitorId);
        setSelectedCompetitors(firstCompetitorId);
        setError(null);
      } else {
        setError('No competitors found for this product. Please add competitors first.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching competitors:', error);
      setError('Error loading competitors. Please try again.');
      setLoading(false);
    }
  };

  const fetchCompetitorAnalysis = async (competitorIds?: string) => {
    if (!data) setLoading(true);
    setApplyLoading(true);
    setError(null);
    try {
      const params: any = {
        days,
        min_search_volume: minSearchVolume,
        max_position: maxPosition,
      };
      const ids = competitorIds || selectedCompetitors;
      if (ids) {
        params.competitor_ids = ids;
      }
      if (market !== '') {
        params.market = market;
      }

      console.log('Fetching competitor analysis with params:', params);
      const data = await api.getCompetitorAnalysis(
        parseInt(productId!),
        params
      );
      console.log('Competitor analysis data received:', data);
      setData(data);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching competitor analysis:', error);
      let errorMessage = 'Error loading competitor analysis. Please try again.';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
        errorMessage = error.response.data?.detail || `Server error: ${error.response.status}`;
        
        if (error.response.status === 503) {
          errorMessage = 'Service temporarily unavailable. Please check if the backend server is running.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication error. Please log in again.';
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        errorMessage = 'No response from server. Please check your connection.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setApplyLoading(false);
    }
  };

  const handleCompetitorChange = (event: SelectChangeEvent<string>) => {
    const newCompetitorId = event.target.value;
    setSelectedCompetitors(newCompetitorId);
    // Automatically apply filters when competitor changes
    fetchCompetitorAnalysis(newCompetitorId);
  };

  const formatCTR = (ctr?: number) => {
    return ctr !== undefined ? `${(ctr * 100).toFixed(1)}%` : 'N/A';
  };

  const getChartData = () => {
    if (!data) return null;

    const labels = showPositionChart
      ? data.position_trends.map(t => t.date)
      : data.ctr_trends.map(t => t.date);

    const datasets = [];

    // Add our product data
    const productData = showPositionChart
      ? data.position_trends.map(t => t.positions[data.product_name] || null)
      : data.ctr_trends.map(t => t.ctr_scores[data.product_name] || null);

    datasets.push({
      label: data.product_name,
      data: productData,
      borderColor: '#4caf50',
      backgroundColor: '#4caf50',
      tension: 0.1,
    });

    // Add competitor data
    const colors = ['#f44336', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4'];
    data.competitors.forEach((competitor, index) => {
      const competitorData = showPositionChart
        ? data.position_trends.map(t => t.positions[competitor.competitor_name] || null)
        : data.ctr_trends.map(t => t.ctr_scores[competitor.competitor_name] || null);

      datasets.push({
        label: competitor.competitor_name,
        data: competitorData,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        tension: 0.1,
      });
    });

    return {
      labels,
      datasets,
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
        title: {
          display: true,
          text: showPositionChart ? 'Position Trends' : 'Total CTR Score Trends',
        },
    },
    scales: {
      y: {
        reverse: showPositionChart,
        beginAtZero: !showPositionChart,
        title: {
          display: true,
          text: showPositionChart ? 'Median Position' : 'Total CTR Score',
        },
      },
    },
  };

  if (loading) {
    return (
      <Box p={3} sx={{ position: 'relative' }}>

        <Typography variant="h4" gutterBottom>
          Competitor Analysis
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} sx={{ position: 'relative' }}>

        <Typography variant="h4" gutterBottom>
          Competitor Analysis
        </Typography>
        <Paper sx={{ p: 3, mt: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          {competitors.length === 0 && (
            <>
              <Typography variant="body1" sx={{ mt: 2 }}>
                To use the competitor analysis feature, you need to:
              </Typography>
              <ol>
                <li>Go back to the product list</li>
                <li>Click on the product to expand it</li>
                <li>Navigate to the "Competitors" tab</li>
                <li>Add at least one competitor ASIN</li>
              </ol>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </>
          )}
        </Paper>
{applyLoading && (
  <Backdrop
    open={false}
    sx={{
      position: 'absolute',
      inset: 0,
      zIndex: (theme) => theme.zIndex.drawer + 1,
      color: '#fff',
      bgcolor: 'rgba(255, 255, 255, 0.6)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <CircularProgress color="primary" />
    <Typography variant="subtitle1" sx={{ mt: 2, color: 'text.secondary' }}>
      Updating data...
    </Typography>
  </Backdrop>
) }
      </Box>
    );
  }

  const chartData = getChartData();

  // Comparison metrics
  const totalKeywords = data ? data.product_performance.keywords_you_lead + data.product_performance.keywords_behind : 0;
  const leadPct = totalKeywords && data ? (data.product_performance.keywords_you_lead / totalKeywords) * 100 : 0;
  const behindPct = 100 - leadPct;
  
  // Calculate position gap with selected competitor
  const selectedCompetitor = data && data.competitors.length > 0
    ? data.competitors.find(c => c.competitor_id.toString() === selectedCompetitors) || data.competitors[0]
    : null;
    
  const positionGap = selectedCompetitor && data
    ? data.product_performance.average_position - selectedCompetitor.performance_summary.average_position
    : null;
  const positionIsWinning = positionGap !== null ? positionGap < 0 : false; // lower position is better
  
  // Calculate CTR comparison
  const ctrGap = selectedCompetitor && data
    ? data.product_performance.total_ctr_score - selectedCompetitor.performance_summary.total_ctr_score
    : null;
  const ctrIsWinning = ctrGap !== null ? ctrGap > 0 : false; // higher CTR is better

  // Sorting handlers
  const handleKeywordSort = (property: string) => {
    const isAsc = keywordSortBy === property && keywordSortDirection === 'asc';
    setKeywordSortDirection(isAsc ? 'desc' : 'asc');
    setKeywordSortBy(property);
  };
  
  const handleOpportunitySort = (property: string) => {
    const isAsc = opportunitySortBy === property && opportunitySortDirection === 'asc';
    setOpportunitySortDirection(isAsc ? 'desc' : 'asc');
    setOpportunitySortBy(property);
  };

  // Sort data based on current sort settings
  const sortKeywordData = (keywordData: any[]) => {
    if (!keywordSortBy) return keywordData;
    
    return [...keywordData].sort((a, b) => {
      let aVal, bVal;
      
      switch (keywordSortBy) {
        case 'keyword':
          aVal = a.keyword?.toLowerCase() || '';
          bVal = b.keyword?.toLowerCase() || '';
          break;
        case 'search_volume':
          aVal = a.search_volume || 0;
          bVal = b.search_volume || 0;
          break;
        case 'your_position':
          aVal = a.your_position || 999;
          bVal = b.your_position || 999;
          break;
        case 'competitor_position':
          // For "All Competitors", get the best competitor position
          if (selectedCompetitors === 'all') {
            const aPositions = Object.values(a.competitor_positions || {}).filter((pos): pos is number => pos !== null && pos !== undefined);
            const bPositions = Object.values(b.competitor_positions || {}).filter((pos): pos is number => pos !== null && pos !== undefined);
            aVal = aPositions.length > 0 ? Math.min(...aPositions) : 999;
            bVal = bPositions.length > 0 ? Math.min(...bPositions) : 999;
          } else {
            const competitorName = data?.competitors.find(c => c.competitor_id.toString() === selectedCompetitors)?.competitor_name;
            aVal = competitorName ? (a.competitor_positions[competitorName] || 999) : 999;
            bVal = competitorName ? (b.competitor_positions[competitorName] || 999) : 999;
          }
          break;
        case 'your_ctr':
          aVal = a.your_ctr || 0;
          bVal = b.your_ctr || 0;
          break;
        case 'competitor_ctr':
          // For "All Competitors", get the best competitor CTR
          if (selectedCompetitors === 'all') {
            const aCtrs = Object.values(a.competitor_ctrs || {}).filter((ctr): ctr is number => ctr !== null && ctr !== undefined);
            const bCtrs = Object.values(b.competitor_ctrs || {}).filter((ctr): ctr is number => ctr !== null && ctr !== undefined);
            aVal = aCtrs.length > 0 ? Math.max(...aCtrs) : 0;
            bVal = bCtrs.length > 0 ? Math.max(...bCtrs) : 0;
          } else {
            const competitorName = data?.competitors.find(c => c.competitor_id.toString() === selectedCompetitors)?.competitor_name;
            aVal = competitorName ? (a.competitor_ctrs[competitorName] || 0) : 0;
            bVal = competitorName ? (b.competitor_ctrs[competitorName] || 0) : 0;
          }
          break;
        default:
          return 0;
      }
      
      if (keywordSortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  };
  
  const sortOpportunityData = (opportunityData: any[]) => {
    if (!opportunitySortBy) return opportunityData;
    
    return [...opportunityData].sort((a, b) => {
      let aVal, bVal;
      
      switch (opportunitySortBy) {
        case 'keyword':
          aVal = a.keyword?.toLowerCase() || '';
          bVal = b.keyword?.toLowerCase() || '';
          break;
        case 'search_volume':
          aVal = a.search_volume || 0;
          bVal = b.search_volume || 0;
          break;
        case 'your_position':
          aVal = a.your_position || 999;
          bVal = b.your_position || 999;
          break;
        case 'competitor_position':
          aVal = a.competitor_position || 999;
          bVal = b.competitor_position || 999;
          break;
        case 'position_gap':
          aVal = a.position_gap || 0;
          bVal = b.position_gap || 0;
          break;
        case 'potential_gain':
          aVal = a.potential_gain || 0;
          bVal = b.potential_gain || 0;
          break;
        default:
          return 0;
      }
      
      if (opportunitySortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  };

  // CSV Export functions
  const handleExportKeywordsCsv = () => {
    if (!data || !data.keyword_comparisons.length) return;
    
    setIsExportingKeywords(true);
    
    try {
      // Prepare CSV headers
      const headers = [
        'Keyword',
        'Search Volume',
        'Your Position',
        'Competitor Position',
        'Your CTR Score',
        'Competitor CTR Score'
      ];
      
      // Get filtered and sorted data
      const filteredData = data.keyword_comparisons.filter((comparison) => {
        if (keywordFilter === 'all') return true;
        
        const ourPosition = comparison.your_position || 999;
        const competitorPositions = comparison.competitor_positions || {};
        const validPositions = Object.values(competitorPositions).filter((pos): pos is number => pos !== null && pos !== undefined);
        
        if (validPositions.length === 0) return false;
        
        const avgCompetitorPosition = validPositions.reduce((sum, pos) => sum + pos, 0) / validPositions.length;
        
        return keywordFilter === 'winning' ? ourPosition < avgCompetitorPosition : ourPosition > avgCompetitorPosition;
      });
      
      const rows = sortKeywordData(filteredData).map((comparison) => {
        // For competitor position and CTR, get the best competitor if "All Competitors" is selected
        let competitorPosition = '-';
        let competitorCtr = '-';
        
        if (selectedCompetitors === 'all') {
          const positions = Object.values(comparison.competitor_positions || {}).filter((pos): pos is number => pos !== null && pos !== undefined);
          const ctrs = Object.values(comparison.competitor_ctrs || {}).filter((ctr): ctr is number => ctr !== null && ctr !== undefined);
          
          if (positions.length > 0) competitorPosition = Math.min(...positions).toString();
          if (ctrs.length > 0) competitorCtr = Math.max(...ctrs).toFixed(0);
        } else {
          const competitorName = data.competitors.find(c => c.competitor_id.toString() === selectedCompetitors)?.competitor_name;
          if (competitorName) {
            competitorPosition = (comparison.competitor_positions[competitorName] || '-').toString();
            competitorCtr = comparison.competitor_ctrs[competitorName] !== undefined && comparison.competitor_ctrs[competitorName] !== null
              ? comparison.competitor_ctrs[competitorName]!.toFixed(0)
              : '-';
          }
        }
        
        return [
          `"${comparison.keyword.replace(/"/g, '""')}"`,
          comparison.search_volume,
          comparison.your_position || '-',
          competitorPosition,
          comparison.your_ctr !== undefined && comparison.your_ctr !== null ? comparison.your_ctr.toFixed(0) : '-',
          competitorCtr
        ];
      });
      
      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filterSuffix = keywordFilter !== 'all' ? `_${keywordFilter}` : '';
      link.download = `keyword_comparison${filterSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting keywords to CSV:', error);
    } finally {
      setIsExportingKeywords(false);
    }
  };
  
  const handleExportOpportunitiesCsv = () => {
    if (!data || !data.top_opportunities.length) return;
    
    setIsExportingOpportunities(true);
    
    try {
      const headers = [
        'Keyword',
        'Search Volume',
        'Your Position',
        'Competitor Position',
        'Position Gap',
        'CTR Score Gap'
      ];
      
      const rows = sortOpportunityData(data.top_opportunities).map((opportunity) => [
        `"${opportunity.keyword.replace(/"/g, '""')}"`,
        opportunity.search_volume,
        opportunity.your_position || '-',
        opportunity.competitor_position || '-',
        opportunity.position_gap !== null ? opportunity.position_gap : 'N/A',
        opportunity.potential_gain
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `top_opportunities_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting opportunities to CSV:', error);
    } finally {
      setIsExportingOpportunities(false);
    }
  };

  return (
    <Box p={3} sx={{ position: 'relative' }}>

      <Typography variant="h4" gutterBottom>
        Competitor Analysis
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Competitor</InputLabel>
                <Select
                  value={selectedCompetitors}
                  onChange={handleCompetitorChange}
                  label="Competitor"
                >
                  <MenuItem value="all">
                    All Competitors
                  </MenuItem>
                  {competitors.map((competitor) => (
                    <MenuItem key={competitor.id} value={competitor.id.toString()}>
                      {competitor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {currentCompetitor && selectedCompetitors !== 'all' && (
                <Tooltip title="View on Amazon">
                  <IconButton
                    href={getAmazonProductUrl(currentCompetitor.asin, typeof market === 'number' ? market : 1)}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <LinkIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Days"
              type="number"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchCompetitorAnalysis();
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Min Search Volume"
              type="number"
              value={minSearchVolume}
              onChange={(e) => setMinSearchVolume(parseInt(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchCompetitorAnalysis();
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Max Position</InputLabel>
              <Select
                value={maxPosition}
                onChange={(e) => setMaxPosition(e.target.value as number)}
                label="Max Position"
              >
                <MenuItem value={10}>Top 10</MenuItem>
                <MenuItem value={50}>Top 50</MenuItem>
                <MenuItem value={100}>Top 100</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Market</InputLabel>
              <Select
                value={market}
                onChange={(e) => setMarket(e.target.value as number | '')}
                label="Market"
              >
                <MenuItem value="">All Markets</MenuItem>
                <MenuItem value={1}>United States</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="contained"
              onClick={() => fetchCompetitorAnalysis()}
              disabled={!selectedCompetitors || applyLoading}
              fullWidth
            >
              {applyLoading ? <CircularProgress size={24} color="inherit" /> : 'Apply'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
{applyLoading && (
  <Backdrop
    open={false}
    sx={{
      position: 'absolute',
      inset: 0,
      zIndex: (theme) => theme.zIndex.drawer + 1,
      color: '#fff',
      bgcolor: 'rgba(255, 255, 255, 0.6)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <CircularProgress color="primary" />
    <Typography variant="subtitle1" sx={{ mt: 2, color: 'text.secondary' }}>
      Updating data...
    </Typography>
  </Backdrop>
) }

      {!loading && data && (
        <>
          {/* Performance Summary */}
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Performance Summary
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ position: 'relative', height: '100%' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Median Position
                  </Typography>
                  <Typography variant="h5" sx={{ display:'flex', alignItems:'center' }}>
                    {Math.round(data.product_performance.average_position)}
                    {positionGap !== null && (
                      positionIsWinning ? <ArrowUpward fontSize="small" sx={{ ml:0.5, color: '#059669' }} />
                      : <ArrowDownward fontSize="small" sx={{ ml:0.5, color: '#DC2626' }} />
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Competitor: {selectedCompetitor ? Math.round(selectedCompetitor.performance_summary.average_position) : 'N/A'}
                  </Typography>
                </CardContent>
            {applyLoading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1 }}>
                <CircularProgress size={28} color="primary" />
              </Box>
            )}
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ position: 'relative', height: '100%' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total CTR Score
                  </Typography>
                  <Typography variant="h5" sx={{ display:'flex', alignItems:'center' }}>
                    {data.product_performance.total_ctr_score.toLocaleString()}
                    {ctrGap !== null && (
                      ctrIsWinning ? <ArrowUpward fontSize="small" sx={{ ml:0.5, color: '#059669' }} />
                      : <ArrowDownward fontSize="small" sx={{ ml:0.5, color: '#DC2626' }} />
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Competitor: {selectedCompetitor ? selectedCompetitor.performance_summary.total_ctr_score.toLocaleString() : 'N/A'}
                  </Typography>
                </CardContent>
            {applyLoading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1 }}>
                <CircularProgress size={28} color="primary" />
              </Box>
            )}
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ position: 'relative', height: '100%' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Keywords You Lead
                  </Typography>
                  <Typography variant="h5">
                    {data.product_performance.keywords_you_lead.toLocaleString()} ({leadPct.toFixed(1)}%)
                  </Typography>
                </CardContent>
            {applyLoading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1 }}>
                <CircularProgress size={28} color="primary" />
              </Box>
            )}
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ position: 'relative', height: '100%' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Keywords Behind
                  </Typography>
                  <Typography variant="h5">
                    {data.product_performance.keywords_behind.toLocaleString()} ({behindPct.toFixed(1)}%)
                  </Typography>
                </CardContent>
            {applyLoading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1 }}>
                <CircularProgress size={28} color="primary" />
              </Box>
            )}
              </Card>
            </Grid>
          </Grid>

          {/* Position & CTR Trends */}
          <Paper sx={{ position: 'relative', p: 2, mb: 3 }}>
            {applyLoading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.6)', zIndex: (theme)=>theme.zIndex.drawer+1 }}>
                <CircularProgress color="primary" />
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Position & CTR Trends</Typography>
              <Box>
                <Button
                  variant={showPositionChart ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowPositionChart(true)}
                  sx={{ mr: 1 }}
                >
                  Position
                </Button>
                <Button
                  variant={!showPositionChart ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowPositionChart(false)}
                >
                  CTR Score
                </Button>
              </Box>
            </Box>
            {chartData && <Line data={chartData} options={chartOptions} />}
            {(!chartData || chartData.datasets.every(d => d.data.every(v => v === null))) && (
              <Typography align="center" color="textSecondary" sx={{ py: 3 }}>
                No data available. Select products to view trend data.
              </Typography>
            )}
          </Paper>
{applyLoading && (
  <Backdrop
    open={false}
    sx={{
      position: 'absolute',
      inset: 0,
      zIndex: (theme) => theme.zIndex.drawer + 1,
      color: '#fff',
      bgcolor: 'rgba(255, 255, 255, 0.6)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <CircularProgress color="primary" />
    <Typography variant="subtitle1" sx={{ mt: 2, color: 'text.secondary' }}>
      Updating data...
    </Typography>
  </Backdrop>
) }

          {/* Keyword Comparison */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              Keyword Comparison
            </Typography>
            <Box>
              <Button
                variant={keywordFilter === 'all' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setKeywordFilter('all')}
                sx={{ mr: 1 }}
              >
                All
              </Button>
              <Button
                variant={keywordFilter === 'winning' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setKeywordFilter('winning')}
                sx={{ mr: 1 }}
              >
                Winning
              </Button>
              <Button
                variant={keywordFilter === 'losing' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setKeywordFilter('losing')}
              >
                Losing
              </Button>
            </Box>
          </Box>
          <TableContainer component={Paper} sx={{ position: 'relative', mb: 3, maxHeight: 600 }}>
            {applyLoading && (
              <Box sx={{ position: 'absolute', inset: 0, display:'flex', justifyContent:'center', alignItems:'center', bgcolor:'rgba(255,255,255,0.6)', zIndex:(theme)=>theme.zIndex.drawer+1 }}>
                <CircularProgress color="primary" size={32}/>
              </Box>
            )}
            <Table>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow sx={{ bgcolor: 'background.paper' }}>
                  <TableCell sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={isExportingKeywords ? 'Exporting...' : 'Export to CSV'}>
                        <IconButton
                          onClick={handleExportKeywordsCsv}
                          disabled={isExportingKeywords || !data.keyword_comparisons.length}
                          size="small"
                          sx={{ p: 0.5, position: 'absolute', left: 4, top: '4px' }}
                        >
                          <FileDownload fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <TableSortLabel
                        active={keywordSortBy === 'keyword'}
                        direction={keywordSortDirection}
                        onClick={() => handleKeywordSort('keyword')}
                        sx={{ ml: 4 }}
                      >
                        Keyword
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={keywordSortBy === 'search_volume'}
                      direction={keywordSortDirection}
                      onClick={() => handleKeywordSort('search_volume')}
                    >
                      Search Volume
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={keywordSortBy === 'your_position'}
                      direction={keywordSortDirection}
                      onClick={() => handleKeywordSort('your_position')}
                    >
                      Your Position
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={keywordSortBy === 'competitor_position'}
                      direction={keywordSortDirection}
                      onClick={() => handleKeywordSort('competitor_position')}
                    >
                      Competitor Position
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={keywordSortBy === 'your_ctr'}
                      direction={keywordSortDirection}
                      onClick={() => handleKeywordSort('your_ctr')}
                    >
                      Your CTR Score
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={keywordSortBy === 'competitor_ctr'}
                      direction={keywordSortDirection}
                      onClick={() => handleKeywordSort('competitor_ctr')}
                    >
                      Competitor CTR Score
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.keyword_comparisons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4 + (data.competitors.length * 2)} align="center">
                      No keywords found with both your products and competitors ranking.
                    </TableCell>
                  </TableRow>
) : (
                  sortKeywordData(
                    data.keyword_comparisons
                      .filter((comparison) => {
                        if (keywordFilter === 'all') return true;
                        
                        // Check if we're winning or losing against any competitor
                        const ourPosition = comparison.your_position || 999;
                        const competitorPositions = comparison.competitor_positions || {};
                        const validPositions = Object.values(competitorPositions).filter((pos): pos is number => pos !== null && pos !== undefined);
                        
                        if (validPositions.length === 0) return false;
                        
                        const avgCompetitorPosition = validPositions.reduce((sum, pos) => sum + pos, 0) / validPositions.length;
                        
                        if (keywordFilter === 'winning') {
                          return ourPosition < avgCompetitorPosition;
                        } else {
                          return ourPosition > avgCompetitorPosition;
                        }
                      })
                  ).map((comparison) => (
                    <TableRow key={comparison.keyword}>
                      <TableCell>{comparison.keyword}</TableCell>
                      <TableCell align="right">
                        {comparison.search_volume.toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        {comparison.your_position || '-'}
                      </TableCell>
                      {data.competitors.map((comp) => (
                        <TableCell key={comp.competitor_id} align="center">
                          {comparison.competitor_positions[comp.competitor_name] || '-'}
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        {comparison.your_ctr !== undefined && comparison.your_ctr !== null ? comparison.your_ctr.toFixed(0) : '-'}
                      </TableCell>
                      {data.competitors.map((comp) => (
                        <TableCell key={`ctr-${comp.competitor_id}`} align="center">
                          {comparison.competitor_ctrs[comp.competitor_name] !== undefined && comparison.competitor_ctrs[comp.competitor_name] !== null
                            ? comparison.competitor_ctrs[comp.competitor_name]!.toFixed(0)
                            : '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Top Opportunities */}
          <Typography variant="h5" gutterBottom>
            Top Opportunities
          </Typography>
          <TableContainer component={Paper} sx={{ position: 'relative', maxHeight: 600 }}>
            {applyLoading && (
              <Box sx={{ position: 'absolute', inset: 0, display:'flex', justifyContent:'center', alignItems:'center', bgcolor:'rgba(255,255,255,0.6)', zIndex:(theme)=>theme.zIndex.drawer+1 }}>
                <CircularProgress color="primary" size={32}/>
              </Box>
            )}
            <Table>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow sx={{ bgcolor: 'background.paper' }}>
                  <TableCell sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={isExportingOpportunities ? 'Exporting...' : 'Export to CSV'}>
                        <IconButton
                          onClick={handleExportOpportunitiesCsv}
                          disabled={isExportingOpportunities || !data.top_opportunities.length}
                          size="small"
                          sx={{ p: 0.5, position: 'absolute', left: 4, top: '4px' }}
                        >
                          <FileDownload fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <TableSortLabel
                        active={opportunitySortBy === 'keyword'}
                        direction={opportunitySortDirection}
                        onClick={() => handleOpportunitySort('keyword')}
                        sx={{ ml: 4 }}
                      >
                        Keyword
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={opportunitySortBy === 'search_volume'}
                      direction={opportunitySortDirection}
                      onClick={() => handleOpportunitySort('search_volume')}
                    >
                      Search Volume
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={opportunitySortBy === 'your_position'}
                      direction={opportunitySortDirection}
                      onClick={() => handleOpportunitySort('your_position')}
                    >
                      Your Position
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={opportunitySortBy === 'competitor_position'}
                      direction={opportunitySortDirection}
                      onClick={() => handleOpportunitySort('competitor_position')}
                    >
                      Competitor Position
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={opportunitySortBy === 'position_gap'}
                      direction={opportunitySortDirection}
                      onClick={() => handleOpportunitySort('position_gap')}
                    >
                      Position Gap
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={opportunitySortBy === 'potential_gain'}
                      direction={opportunitySortDirection}
                      onClick={() => handleOpportunitySort('potential_gain')}
                    >
                      CTR Score Gap
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.top_opportunities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No opportunities found. Select products to view opportunity data.
                    </TableCell>
                  </TableRow>
) : (
                  sortOpportunityData(data.top_opportunities).map((opportunity) => (
                    <TableRow key={opportunity.keyword}>
                      <TableCell>{opportunity.keyword}</TableCell>
                      <TableCell align="right">
                        {opportunity.search_volume.toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        {opportunity.your_position || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {opportunity.competitor_position || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {opportunity.position_gap !== null ? opportunity.position_gap : 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        {opportunity.potential_gain}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default CompetitorAnalysis;